import React from 'react';
import Box from '@mui/joy/Box';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import Accordion from '@mui/joy/Accordion';
import AccordionDetails from '@mui/joy/AccordionDetails';
import AccordionSummary from '@mui/joy/AccordionSummary';
import AccordionGroup from '@mui/joy/AccordionGroup';
import CircularProgress from '@mui/joy/CircularProgress';
import IconButton from '@mui/joy/IconButton';
import {
  ArrowLeft as ArrowBackRounded,
  CheckCircle as CheckCircleOutlineRounded,
  ArrowsClockwise as RefreshRounded,
  Robot as SmartToyOutlined,
} from '@phosphor-icons/react';
import { formatDistanceToNow, format } from 'date-fns';
import { useTicket, useCloseTicket, useReopenTicket, useResolveTicket } from '../../hooks/useSupport';
import { StatusChip, PriorityChip } from './TicketStatusChip';
import TicketMessageThread from './TicketMessageThread';
import TicketReplyComposer from './TicketReplyComposer';
import { useAuth } from '../../contexts/AuthContext';

interface Props {
  ticketId: string;
  onBack?: () => void;
  compact?: boolean;
}

export default function TicketDetail({ ticketId, onBack, compact = false }: Props) {
  const { user } = useAuth();
  const { data: ticket, isLoading } = useTicket(ticketId);
  const closeTicket = useCloseTicket();
  const reopenTicket = useReopenTicket();
  const resolveTicket = useResolveTicket();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size="sm" />
      </Box>
    );
  }

  if (!ticket) return null;

  const isResolved = ticket.status === 'resolved' || ticket.status === 'closed';
  const isSuperUser = user?.is_superuser ?? false;

  // Compact mode: scrollable single column, reply pinned at bottom
  if (compact) {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Scrollable area: header + description + messages */}
        <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {/* Header */}
          <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              {onBack && (
                <IconButton size="sm" variant="plain" color="neutral" onClick={onBack} sx={{ flexShrink: 0 }}>
                  <ArrowBackRounded size={18} />
                </IconButton>
              )}
              <Stack sx={{ flex: 1, minWidth: 0 }}>
                <Typography level="body-sm" sx={{ fontWeight: 700 }} noWrap>
                  {ticket.subject}
                </Typography>
                <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                  <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                    {ticket.ticket_number}
                  </Typography>
                  <StatusChip status={ticket.status} />
                  <PriorityChip priority={ticket.priority} />
                </Stack>
              </Stack>
            </Stack>

            {/* Inline action buttons */}
            {isSuperUser && !isResolved && (
              <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, ml: onBack ? 4.5 : 0 }}>
                <Button size="sm" variant="soft" color="success"
                  startDecorator={<CheckCircleOutlineRounded size={14} />}
                  onClick={() => resolveTicket.mutate(ticketId)}
                  loading={resolveTicket.isPending}
                  sx={{ fontSize: '0.7rem', py: 0.25 }}
                >Resolve</Button>
                <Button size="sm" variant="soft" color="neutral"
                  onClick={() => closeTicket.mutate(ticketId)}
                  loading={closeTicket.isPending}
                  sx={{ fontSize: '0.7rem', py: 0.25 }}
                >Close</Button>
              </Stack>
            )}
            {isResolved && (
              <Stack spacing={0.5} sx={{ mt: 0.5, ml: onBack ? 4.5 : 0 }}>
                {ticket.resolved_at && (
                  <Typography level="body-xs" sx={{ color: 'success.600' }}>
                    Resolved {format(new Date(ticket.resolved_at), 'MMM d, yyyy h:mm a')}
                    {ticket.resolved_by && ` by ${ticket.resolved_by.first_name} ${ticket.resolved_by.last_name}`.trim()}
                  </Typography>
                )}
                {ticket.closed_at && ticket.status === 'closed' && (
                  <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                    Closed {format(new Date(ticket.closed_at), 'MMM d, yyyy h:mm a')}
                  </Typography>
                )}
                <Stack direction="row" spacing={0.5}>
                  <Button size="sm" variant="outlined" color="primary"
                    startDecorator={<RefreshRounded size={14} />}
                    onClick={() => reopenTicket.mutate(ticketId)}
                    loading={reopenTicket.isPending}
                    sx={{ fontSize: '0.7rem', py: 0.25 }}
                  >Reopen</Button>
                </Stack>
              </Stack>
            )}
          </Box>

          {/* Description */}
          <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography level="body-xs" sx={{ fontWeight: 600, mb: 0.5 }}>Description</Typography>
            <Typography level="body-xs" sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary' }}>
              {ticket.description}
            </Typography>
          </Box>

          {/* AI context (if any) */}
          {ticket.ai_conversation_context && (
            <AccordionGroup size="sm">
              <Accordion>
                <AccordionSummary sx={{ py: 0.5, px: 1.5 }}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <SmartToyOutlined size={12} />
                    <Typography level="body-xs" sx={{ fontWeight: 600 }}>AI context</Typography>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 1.5 }}>
                  <Typography level="body-xs" sx={{ whiteSpace: 'pre-wrap', color: 'text.tertiary' }}>
                    {ticket.ai_conversation_context}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </AccordionGroup>
          )}

          {/* Messages */}
          <Box sx={{ px: 1.5, py: 1 }}>
            <TicketMessageThread ticketId={ticketId} currentUserId={user?.id} />
          </Box>
        </Box>

        {/* Reply composer — pinned at bottom */}
        {!isResolved ? (
          <Box sx={{ px: 1.5, pb: 1.5, pt: 0.5, flexShrink: 0, borderTop: '1px solid', borderColor: 'divider' }}>
            <TicketReplyComposer key={ticketId} ticketId={ticketId} isSuperUser={isSuperUser} />
          </Box>
        ) : (
          <Box sx={{ px: 1.5, pb: 1.5, pt: 1, textAlign: 'center', flexShrink: 0, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
              Ticket {ticket.status}. Reopen for further help.
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  // Full-page mode
  return (
    <Stack sx={{ height: '100%' }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
        {onBack && (
          <Button
            variant="plain"
            color="neutral"
            size="sm"
            startDecorator={<ArrowBackRounded />}
            onClick={onBack}
            sx={{ mb: 1, ml: -1 }}
          >
            Back
          </Button>
        )}
        <Stack spacing={1}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
            <Typography level="title-md" sx={{ fontWeight: 700, flex: 1 }}>
              {ticket.subject}
            </Typography>
            <StatusChip status={ticket.status} />
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>{ticket.ticket_number}</Typography>
            <PriorityChip priority={ticket.priority} />
            {ticket.organization_name && (
              <Typography level="body-xs" sx={{ color: 'text.secondary' }}>{ticket.organization_name}</Typography>
            )}
            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
              {formatDistanceToNow(new Date(ticket.created_at), { addSuffix: true })}
            </Typography>
          </Stack>

          {/* Description */}
          <AccordionGroup size="sm">
            <Accordion defaultExpanded>
              <AccordionSummary>
                <Typography level="body-sm" sx={{ fontWeight: 600 }}>Description</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap' }}>{ticket.description}</Typography>
              </AccordionDetails>
            </Accordion>
            {ticket.ai_conversation_context && (
              <Accordion>
                <AccordionSummary>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <SmartToyOutlined size={14} />
                    <Typography level="body-xs">AI conversation context</Typography>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography level="body-xs" sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary' }}>
                    {ticket.ai_conversation_context}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            )}
          </AccordionGroup>

          {/* Actions */}
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {!isResolved && isSuperUser && (
              <Button
                size="sm" variant="soft" color="success"
                startDecorator={<CheckCircleOutlineRounded />}
                onClick={() => resolveTicket.mutate(ticketId)}
                loading={resolveTicket.isPending}
              >
                Mark Resolved
              </Button>
            )}
            {ticket.status !== 'closed' && isSuperUser && (
              <Button size="sm" variant="soft" color="neutral"
                onClick={() => closeTicket.mutate(ticketId)}
                loading={closeTicket.isPending}
              >
                Close Ticket
              </Button>
            )}
            {isResolved && (
              <>
                <Button size="sm" variant="outlined" color="primary"
                  startDecorator={<RefreshRounded />}
                  onClick={() => reopenTicket.mutate(ticketId)}
                  loading={reopenTicket.isPending}
                >
                  Reopen
                </Button>
              </>
            )}
          </Stack>

          {isResolved && (ticket.resolved_at || ticket.closed_at) && (
            <Stack spacing={0.25}>
              {ticket.resolved_at && (
                <Typography level="body-xs" sx={{ color: 'success.600' }}>
                  Resolved on {format(new Date(ticket.resolved_at), 'MMM d, yyyy h:mm a')}
                  {ticket.resolved_by && ` by ${ticket.resolved_by.first_name} ${ticket.resolved_by.last_name}`.trim()}
                </Typography>
              )}
              {ticket.closed_at && ticket.status === 'closed' && (
                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                  Closed on {format(new Date(ticket.closed_at), 'MMM d, yyyy h:mm a')}
                  {ticket.closed_by && ` by ${ticket.closed_by.first_name} ${ticket.closed_by.last_name}`.trim()}
                </Typography>
              )}
            </Stack>
          )}
        </Stack>
      </Box>

      {/* Messages */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1, minHeight: 0 }}>
        <TicketMessageThread ticketId={ticketId} currentUserId={user?.id} />
      </Box>

      {/* Reply composer */}
      {!isResolved ? (
        <Box sx={{ px: 2, pb: 2, flexShrink: 0 }}>
          <TicketReplyComposer key={ticketId} ticketId={ticketId} isSuperUser={isSuperUser} />
        </Box>
      ) : (
        <Box sx={{ px: 2, pb: 2, textAlign: 'center', flexShrink: 0 }}>
          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
            This ticket is {ticket.status}. Reopen it if you need further assistance.
          </Typography>
        </Box>
      )}
    </Stack>
  );
}
